// AdminWastedWarmLeadsPage.js — surfaces leads that toured or received
// pricing but haven't been contacted for N days. Master-admin only.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Flame, RefreshCw, Loader2, AlertCircle, ExternalLink,
  Phone, Mail, User, Calendar, DollarSign, Ban, ShieldOff,
} from 'lucide-react';
import CallButton from '../../components/CallButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return iso; }
};

const AdminWastedWarmLeadsPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [minDays, setMinDays] = useState(21);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`, { headers });
        const rows = Array.isArray(r.data) ? r.data : (r.data.domains || []);
        setDomains(rows);
        const tf = rows.find((d) => (d.name || '').toLowerCase().includes('total fusion') || (d.yabbit_domain || '').toLowerCase().includes('totalfusion'));
        if (tf) setDomainId(tf.id);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load domains.');
      }
    })();
  }, [headers]);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!domainId) return;
    force ? setScanning(true) : setLoading(true);
    setErr('');
    try {
      const r = await axios.get(
        `${API}/admin/wasted-warm-leads?domain_id=${domainId}&min_days=${minDays}${force ? '&force=true' : ''}`,
        { headers, timeout: 300000 },
      );
      setSnap(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setErr('GymSales integration is disabled or misconfigured. Turn it on in backend/.env → GYMSALES_ENABLED=true.');
      } else {
        setErr(e?.response?.data?.detail || 'Scan failed.');
      }
      setSnap(null);
    } finally {
      setLoading(false); setScanning(false);
    }
  }, [domainId, minDays, headers]);

  useEffect(() => { if (domainId) load(); }, [domainId, minDays, load]);

  const matches = snap?.matches || [];

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="wasted-warm-page">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-400" />
              Wasted Warm Leads
            </h1>
            <p className="text-sm text-white/60 mt-1 max-w-3xl">
              Leads in GymSales who <b>toured</b> or <b>received pricing</b> but have had no consultant contact for {minDays}+ days.
              These are the highest-value revivable deals — they&apos;ve already been in the door and seen the price.
            </p>
          </div>
          <button
            onClick={() => load({ force: true })}
            disabled={loading || scanning || !domainId}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold rounded-lg"
            data-testid="wasted-warm-rescan"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {scanning ? 'Scanning…' : 'Re-scan now'}
          </button>
        </div>

        {/* Domain + days picker */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 flex items-center flex-wrap gap-3">
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold">Domain</label>
          <select
            value={domainId}
            onChange={(e) => setDomainId(e.target.value)}
            className="bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1 min-w-[200px]"
            data-testid="wasted-warm-domain-picker"
          >
            <option value="">— select —</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.name || d.yabbit_domain}</option>
            ))}
          </select>
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold ml-3">Silence &ge;</label>
          <select
            value={minDays}
            onChange={(e) => setMinDays(Number(e.target.value))}
            className="bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-28"
            data-testid="wasted-warm-days-picker"
          >
            {[14, 21, 30, 45, 60, 90].map((n) => <option key={n} value={n}>{n} days</option>)}
          </select>
          {snap && (
            <div className="text-white/50 text-xs ml-auto">
              Scanned {snap.scanned || 0} leads · {snap.from_cache ? 'cached' : 'fresh'} · {snap.scanned_at ? new Date(snap.scanned_at).toLocaleString() : ''}
            </div>
          )}
        </div>

        {err && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{err}
          </div>
        )}

        {loading && !snap ? (
          <div className="text-center py-12 text-white/50">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Loading…
          </div>
        ) : matches.length === 0 && snap ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 text-center text-white/50">
            <Flame className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-white/80 font-semibold">No wasted warm leads found</div>
            <div className="text-white/50 text-sm mt-1">
              {snap.scanned === 0
                ? 'The tenant has no leads in GymSales yet. Try again once real data flows in.'
                : `Scanned ${snap.scanned} leads — nobody matches the criteria (toured or priced + ${minDays}+ days silent). Great pipeline hygiene!`}
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            {matches.map((m) => (
              <div key={m.gymsales_lead_id} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid={`wasted-warm-row-${m.gymsales_lead_id}`}>
                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-orange-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold flex items-center gap-2 flex-wrap">
                    {[m.first_name, m.last_name].filter(Boolean).join(' ') || '(no name)'}
                    {m.toured && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-teal-500/20 text-teal-300 font-semibold uppercase tracking-wider">
                        <Calendar className="w-3 h-3" />Toured
                      </span>
                    )}
                    {m.pricing_sent && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-300 font-semibold uppercase tracking-wider">
                        <DollarSign className="w-3 h-3" />Priced
                      </span>
                    )}
                  </div>
                  <div className="text-white/50 text-xs flex items-center gap-3 flex-wrap mt-0.5">
                    {m.phone_mobile && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone_mobile}</span>}
                    {m.email && <span className="inline-flex items-center gap-1 truncate max-w-xs"><Mail className="w-3 h-3" />{m.email}</span>}
                    {m.assigned_to && <span>→ {m.assigned_to}</span>}
                    {m.status && <span className="text-white/40">· {m.status}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-orange-300 font-bold text-lg leading-none">{m.days_since_last_contact}<span className="text-xs text-white/50 ml-1">d</span></div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">silent</div>
                </div>
                <div className="text-right shrink-0 text-[10px] text-white/40 min-w-[70px]">
                  {m.last_tour_at && <div>Tour: {_fmtDate(m.last_tour_at)}</div>}
                  {m.last_pricing_at && <div>Price: {_fmtDate(m.last_pricing_at)}</div>}
                </div>
                {m.deep_link && (
                  <a
                    href={m.deep_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-semibold rounded shrink-0"
                  >
                    Open in GymSales <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {m.phone_mobile && (
                  <CallButton
                    destination={m.phone_mobile}
                    reason="wasted_warm_lead"
                    context={{ gymsales_lead_id: m.gymsales_lead_id }}
                    size="sm"
                    label="Call"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminWastedWarmLeadsPage;
